import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import {
  catchError,
  endWith,
  filter,
  map,
  of,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs'

import { ConversationActions } from '@flowx/designer/modules/ai/conversation/conversation.actions'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'

import { AiAssistantActions } from './assistant.actions'

import { AgentAction, AgentName } from '../../../conversation/ai-conversation.commons'
import { AiConversationService } from '../../../conversation/ai-conversation.service'
import { AiActions, AiConversationServiceActions } from '../../../store/ai.actions'
import { AiSelectors } from '../../../store/ai.selectors'
import { AiAssistantAPI, AnswerPayload } from '../api/assistant.api'

@Injectable()
export class AiAssistantEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiAssistantAPI = inject(AiAssistantAPI)
  #aiConversationService = inject(AiConversationService)

  getAnswer = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiAssistantActions.getAnswer),
      withLatestFrom(this.#store.select(AiSelectors.sessionId)),
      map(
        ([{ prompt }, sessionId]) =>
          ({ question: prompt, session_id: sessionId }) satisfies AnswerPayload
      ),
      switchMap((payload) =>
        this.#aiAssistantAPI.getAnswer(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiAssistantActions.getAnswerEventStream({ response })),
          catchError(({ status }) =>
            of(
              AiAssistantActions.getAnswerStreamFailure({
                statusCode: status,
                errorMsg: null,
              })
            )
          ),
          endWith(AiAssistantActions.getAnswerStreamCompleted())
        )
      )
    )
  )

  sendFeedback = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiAssistantActions.sendFeedback),
        switchMap(({ payload }) => this.#aiAssistantAPI.sendFeedback(payload))
      ),
    { dispatch: false }
  )

  discardHintedAssistantThread = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiConversationServiceActions.conversationMessageStreamEnded),
        withLatestFrom(this.#store.select(ConversationSelectors.currentMessageAgent)),
        filter(
          ([, currentMessageAgent]) =>
            currentMessageAgent?.agent === AgentName.AiAssistant &&
            currentMessageAgent?.action === AgentAction.AskAssistant
        ),
        // Wait for the agent thread refetch (reloadAgentThread) to complete before discarding.
        // Both this effect and reloadAgentThread trigger on conversationMessageStreamEnded.
        // Without waiting, the discard sets the thread to FINISHED via discardThreadApiResponse,
        // but the concurrent fetch returns stale PROCESSING data and overwrites it.
        switchMap(() =>
          this.#actions$.pipe(ofType(ConversationActions.threadMessageFetchSuccess), take(1))
        ),
        // Guard: skip if the thread was already discarded (e.g. by discardAutoStartedAssistantThread)
        filter(() => this.#store.selectSignal(ConversationSelectors.activeAgentThreadExists)()),
        switchMap(() => this.#aiConversationService.discardThread(false))
      ),
    { dispatch: false }
  )

  discardAutoStartedAssistantThread = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationActions.autoStartAssistantAction),
        switchMap(() =>
          this.#actions$.pipe(
            ofType(AiConversationServiceActions.conversationMessageStreamEnded),
            take(1),
            switchMap(() => this.#aiConversationService.discardThread(false))
          )
        )
      ),
    { dispatch: false }
  )
}
